import { useEffect, useMemo, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { useField } from 'formik';
import { getCustomStyles } from '@/theme/components/select';
import { createLabelForSelect } from '../../SelectComponents';
import { useCreatableSelectHelpers } from './creatableSelectHelpers';
import { KSelectStylesConfig } from '@/ui-library/design-system/constants/KSelect.styles';

export const CreatableSelectField = ({ item, field, formik = {} }) => {
  const [selected, setSelected] = useState(null);
  const {
    name,
    options = [],
    selected: defaultValue,
    createPlaceHolder = 'Create as new option and add',
    isMulti = false,
    components = {},
    disabled,
    isClearable = false,
    newStyles = [],
    getCustomOptions = () => [],
    customOnChange,
    formatCreateLabel,
    menuPlacement,
    styleVersion = 'v1',
  } = item;

  const [_field, meta, helpers] = useField(name); // eslint-disable-line
  const [isLoading, setIsLoading] = useState(false);
  const { error, touched } = meta;

  const styles = KSelectStylesConfig({
    variant: 'default',
    customStyles: newStyles,
  });

  const styleVersionMap = {
    v1: getCustomStyles(newStyles),
    v2: styles,
  };

  const selectStyles = styleVersionMap[styleVersion] || styles;

  const customOptions = getCustomOptions?.();

  const allOptions = useMemo(() => {
    return [...options, ...(customOptions || [])];
  }, [options, customOptions]);

  const { submitCount } = formik || {};
  const showError = (submitCount > 0 || touched) && error;

  const {
    onChange,
    onCreate,
    formatOptionLabel,
    isValidNewOption,
    NoOptionsMessageComp,
  } = useCreatableSelectHelpers({
    item,
    setSelected,
    setIsLoading,
  });

  const currentFormik = meta?.value;

  useEffect(() => {
    setSelected((prev) => {
      // Guard against empty values
      if (!currentFormik) return null;

      const isSameValue = prev?.value === currentFormik;
      if (isSameValue) return prev;

      const currentOption = allOptions?.find(
        (option) => option?.value === currentFormik
      );
      if (currentOption) return currentOption;

      // Don't create a new option if currentFormik is empty
      if (currentFormik === '' || currentFormik === null) return null;

      const newCreatableOption = { label: currentFormik, value: currentFormik };
      return newCreatableOption;
    });
  }, [allOptions, currentFormik, setSelected]);

  useEffect(() => {
    if (!defaultValue) return;
    setSelected({
      label: defaultValue?.label,
      value: defaultValue?.value,
      ...defaultValue,
    });
  }, [defaultValue]);

  const handleOnChange = (props) => {
    onChange(props);
    customOnChange?.(props);
  };

  const onFormatCreateLabel = (args) => {
    if (formatCreateLabel) return formatCreateLabel(args);
    return createLabelForSelect(args, createPlaceHolder);
  };

  return (
    <CreatableSelect
      options={allOptions}
      isLoading={isLoading}
      value={selected}
      inputId={name}
      hasError={showError}
      onChange={handleOnChange}
      onBlur={field?.onBlur}
      className="kula_custom_select__container"
      classNamePrefix="kula_custom_select"
      onCreateOption={onCreate}
      styles={selectStyles}
      menuPlacement={menuPlacement}
      components={{
        IndicatorSeparator: null,
        NoOptionsMessage: NoOptionsMessageComp,
        ...components,
      }}
      isDisabled={disabled}
      formatOptionLabel={formatOptionLabel}
      isValidNewOption={isValidNewOption}
      createOptionPosition="first"
      formatCreateLabel={onFormatCreateLabel}
      placeholder={item.placeholder || 'Select or create an option...'}
      isClearable={isClearable}
      isSearchable
      isMulti={isMulti}
      extraProps={{
        ...item,
      }}
    />
  );
};

//todo: enable in future if needed

// const allOptions = useMemo(
//   () => [...staticOptions, ...optionsData],
//   [staticOptions, optionsData]
// );

// const [optionsData, setOptionsData] = useState([]);

// useEffect(() => {
//   const getOptionsFromApi = async () => {
//     setIsLoading(true);
//     try {
//       if (fetchConfig?.endPoint) {
//         const res = await fetchSelectOptions(fetchConfig);
//         // res has to be a array of objects
//         if (Array.isArray(res)) {
//           setOptionsData(res);
//           setInitialValues(res);
//         }
//       }
//     } catch (error) {
//       console.error(`Error fetching ${name} options`, error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   getOptionsFromApi();
//   // eslint-disable-next-line react-hooks/exhaustive-deps
// }, [fetchConfig]);

// const setInitialValues = (optionFromApi) => {
//   const matchingOptions = [...staticOptions, ...optionFromApi].filter(
//     (option) => initialValue.includes(option.value)
//   );
//   // Create new options for values in initialValue that are not found in matchingOptions
//   const newOptions = initialValue
//     .filter(
//       (value) => !matchingOptions.some((option) => option.value === value)
//     )
//     .map((value) => ({ label: value, value }));

//   const combinedOptions = [...matchingOptions, ...newOptions];
//   setSelected(combinedOptions);
// };
